"use client";
import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";

interface TypewriterOptions {
  /** Milliseconds per character while typing. */
  speed?: number;
  /** Pause before the first character, e.g. to let an image settle in. */
  startDelay?: number;
  /** Keep going forever: type → hold → erase → retype. Default types once. */
  loop?: boolean;
  /** How long to sit on the full (and, briefly, the empty) string per pass. */
  holdMs?: number;
}

// Reveals a string one character at a time whenever `active` turns true, and
// clears it when `active` turns false — a state-triggered typewriter.
//
// Deliberately NOT the scroll-scrubbed reveal the Case Study heading uses: that
// one is driven by a scroll MotionValue, this one plays on its own once a thing
// becomes active (a slab opening, a link on screen). Returns the number of
// visible characters; the caller slices its own text so the markup stays its.
//
// With `loop`, it never settles: after the full string it holds, erases back to
// nothing, holds, and types again — an endless typewriter. Without it (default)
// it types once and stops, which is what the service rail relies on.
//
// The reset is done during render, on the active→ transition, rather than in an
// effect — React's supported "adjust state on prop change" pattern, which avoids
// both a one-frame flash of the previous string and the set-state-in-effect the
// linter forbids.
export const useTypewriter = (
  text: string,
  active: boolean,
  {
    speed = 34,
    startDelay = 0,
    loop = false,
    holdMs = 1400,
  }: TypewriterOptions = {},
) => {
  const reduceMotion = useReducedMotion();
  const [count, setCount] = useState(0);
  const [prevActive, setPrevActive] = useState(active);

  if (active !== prevActive) {
    setPrevActive(active);
    setCount(0);
  }

  useEffect(() => {
    if (!active || reduceMotion) return;

    let raf = 0;
    let shown = 0;
    let last = 0;
    let started = false;
    let phase: "typing" | "holdFull" | "erasing" | "holdEmpty" = "typing";
    let holdUntil = 0;
    const eraseSpeed = Math.max(12, speed * 0.6);

    const tick = (now: number) => {
      if (!started) {
        last = now;
        started = true;
        raf = requestAnimationFrame(tick);
        return;
      }

      if (phase === "typing") {
        const due = Math.floor((now - last) / speed);
        if (due > 0) {
          shown = Math.min(shown + due, text.length);
          setCount(shown);
          last = now;
        }
        if (shown >= text.length) {
          if (!loop) return; // typed once — stop the loop entirely
          phase = "holdFull";
          holdUntil = now + holdMs;
        }
      } else if (phase === "holdFull") {
        if (now >= holdUntil) {
          phase = "erasing";
          last = now;
        }
      } else if (phase === "erasing") {
        const due = Math.floor((now - last) / eraseSpeed);
        if (due > 0) {
          shown = Math.max(shown - due, 0);
          setCount(shown);
          last = now;
        }
        if (shown <= 0) {
          phase = "holdEmpty";
          holdUntil = now + holdMs * 0.4;
        }
      } else if (phase === "holdEmpty") {
        if (now >= holdUntil) {
          phase = "typing";
          last = now;
        }
      }

      raf = requestAnimationFrame(tick);
    };

    const startTimer = setTimeout(() => {
      raf = requestAnimationFrame(tick);
    }, startDelay);

    return () => {
      clearTimeout(startTimer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active, text, speed, startDelay, loop, holdMs, reduceMotion]);

  // Reduced motion (or an inactive slab) shows the whole string at once — the
  // animation is the flourish, never the only way to read it.
  if (!active) return 0;
  if (reduceMotion) return text.length;
  return count;
};
